import { formatDate } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  delay,
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UserContactService } from '../services';
import {
  AuthService,
  CustomValidators,
  LookupService,
  Nullable,
  Unity4Claim,
  collator,
  requiredIfValidator,
  shareReplayRefCount,
  valuesOf,
  ResetPasswordModalComponent,
} from '@adreno-common';
import { environment } from '@environments/environment';
import { EventEmitter } from '@angular/core';
import {
  UploadOutput,
  UploadInput,
  UploadFile,
  humanizeBytes,
  UploaderOptions,
} from 'ngx-uploader';
import { ResetAvatarConfirmationComponent } from './reset-avatar-confirmation.component';
import { GetUserContactDetailsVm } from '../models';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-user-contact',
  templateUrl: './user-contact.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-grow: 1;
      }

      .form-group > label {
        font-size: small;
      }

      .sectionheading {
        margin-bottom: -0.5em;
      }

      .large-avatar {
        min-width: 100px;
        max-width: 100px;
        min-height: 100px;
        max-height: 100px;
        overflow: hidden;
      }

      .avatar-upload-overlay {
        position: relative;
        top: -100px;
        margin-bottom: -100px;
      }

      .avatar-upload {
        height: 100px;
        width: 100px;
        opacity: 0;
        filter: alpha(opacity=0);
        cursor: pointer;
      }

      .avatar-reset {
        width: 100px;
        max-width: 100px;
      }

      .w-4rem {
        width: 4rem;
      }

      .w-6rem {
        width: 6rem;
      }

      .w-8rem {
        width: 8rem;
      }

      .w-105rem {
        width: 10.5rem;
      }

      .w-12rem {
        width: 12rem;
      }

      .w-15rem {
        width: 15rem;
      }

      .w-16rem {
        width: 16rem;
      }

      .w-17rem {
        width: 17rem;
      }

      .w-18rem {
        width: 18rem;
      }

      .w-22rem {
        width: 22rem;
      }

      .w-27rem {
        width: 27rem;
      }

      .w-35rem {
        width: 35rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserContactComponent implements OnInit {
  @ViewChild('avatarUpload') avatarUpload?: ElementRef<HTMLInputElement>;

  private contactDetails$!: Observable<GetUserContactDetailsVm & CountryFlags>;
  private subs = new SubSink();
  private usersApi = `${environment.apiPath}/users`;

  Unity4Claim = Unity4Claim;

  collator = collator;
  dragOver: boolean = false;
  files: UploadFile[] = [];
  formGroup$!: Observable<FormGroup>;
  humanizeBytes = humanizeBytes;
  maxDob = new Date(new Date().getFullYear() - 10, 11, 31);
  minDob = new Date(new Date().getFullYear() - 100, 0, 1);
  options: UploaderOptions = {
    concurrency: 1,
    maxUploads: 3,
    maxFileSize: 10000000,
  };
  uploadInput = new EventEmitter<UploadInput>();
  userId$ = this.authSvc.userId$.pipe(
    filter((userId) => !!userId),
    map((userId) => userId!)
  );
  viewModel$!: Observable<ViewModel>;

  constructor(
    private fb: FormBuilder,
    private userSvc: UserContactService,
    private toastr: ToastrService,
    private modalSvc: NgbModal,
    private authSvc: AuthService,
    private lookupSvc: LookupService
  ) {}

  private initFormGroup() {
    this.formGroup$ = this.contactDetails$.pipe(
      withLatestFrom(this.authSvc.hasClaim$(Unity4Claim.AccountDetails)),
      take(1),
      map(([cd, accountDetailsClaim]) => {
        const phone1Validator = requiredIfValidator(
          () => !fg.get('mobile')?.value
        );
        const mobileValidator = requiredIfValidator(
          () => !fg.get('phone1')?.value
        );
        const dobvalidator = requiredIfValidator(() => accountDetailsClaim);

        const baseConfig: {
          [key: string]: any;
        } = {
          salutation: [cd.salutation, [Validators.maxLength(50)]],
          first: [cd.first, [Validators.required, Validators.maxLength(50)]],
          last: [cd.last, [Validators.required, Validators.maxLength(50)]],
          dob: [
            {
              value: cd.dob ? formatDate(cd.dob, 'yyyy-MM-dd', 'en') : null,
              disabled: cd.dob,
            },
            [
              dobvalidator,
              CustomValidators.minDate(this.minDob),
              CustomValidators.maxDate(this.maxDob),
            ],
          ],
          address: [
            cd.address,
            [Validators.required, Validators.maxLength(50)],
          ],
          address2: [cd.address2, [Validators.maxLength(50)]],
          suburb: [cd.suburb, [Validators.required, Validators.maxLength(50)]],
          state: [cd.state, [Validators.required, Validators.maxLength(50)]],
          postcode: [
            cd.postcode,
            [Validators.required, Validators.maxLength(50)],
          ],
          country: [
            cd.country,
            [Validators.required, Validators.maxLength(50)],
          ],
          phone1: [
            cd.phone1,
            [
              Validators.pattern(/^0[23478]\d{8}$/),
              Validators.maxLength(50),
              phone1Validator,
            ],
          ],
          mobile: [
            cd.mobile,
            [
              Validators.pattern(/^0[23478]\d{8}$/),
              Validators.maxLength(50),
              mobileValidator,
            ],
          ],
          telephony: [
            cd.telephony,
            [Validators.pattern(/^0[2378]\d{8}$/), Validators.maxLength(50)],
          ],
          email: [
            cd.email,
            [Validators.required, Validators.email, Validators.maxLength(50)],
          ],
          emergencyName: [cd.emergencyName, [Validators.maxLength(500)]],
          emergencyRelationship: [
            cd.emergencyRelationship,
            [Validators.maxLength(50)],
          ],
          emergencyContact: [cd.emergencyContact, [Validators.maxLength(50)]],
          accountName: [cd.accountName, [Validators.maxLength(50)]],
          bsb: [cd.bsb, [Validators.maxLength(50)]],
          accountNumber: [
            cd.accountNumber,
            [Validators.maxLength(10), Validators.pattern(/^\d+$/)],
          ],
          bank: [cd.bank, [Validators.maxLength(50)]],
          branch: [cd.branch, [Validators.maxLength(50)]],
        };

        let fgConfig:
          | {
              [key: string]: any;
            }
          | undefined;
        let fg: FormGroup;
        switch (true) {
          case cd.isNZ: {
            const bankDetailsPredicate = () =>
              fg.get('accountName')?.value ||
              fg.get('bank')?.value ||
              fg.get('branch')?.value ||
              fg.get('accountNumber')?.value ||
              fg.get('bsb')?.value;
            fgConfig = {
              ...baseConfig,
              ...{
                state: [cd.state, [Validators.maxLength(50)]],
                phone1: [
                  cd.phone1,
                  [Validators.pattern(/^0[234679]\d{7,9}$/), phone1Validator],
                ],
                mobile: [
                  cd.mobile,
                  [Validators.pattern(/^0[234679]\d{7,9}$/), mobileValidator],
                ],
                telephony: [
                  cd.telephony,
                  [Validators.pattern(/^0[34679]\d{7}$/)],
                ],
                accountName: [
                  cd.accountName,
                  [
                    Validators.maxLength(50),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                bank: [
                  cd.bsb?.slice(0, 2),
                  [
                    Validators.pattern(/^\d{2}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                branch: [
                  cd.bsb?.slice(2, 6),
                  [
                    Validators.pattern(/^\d{4}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                accountNumber: [
                  cd.accountNumber?.slice(0, 7),
                  [
                    Validators.pattern(/^\d{7}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                bsb: [
                  cd.accountNumber?.slice(7, 9),
                  [
                    Validators.pattern(/^\d{2}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
              },
            };
            break;
          }
          case cd.isUK:
            fgConfig = {
              ...baseConfig,
              ...{
                phone1: [
                  cd.phone1,
                  [Validators.pattern(/^0[1-357]\d{8,9}$/), phone1Validator],
                ],
                mobile: [
                  cd.mobile,
                  [Validators.pattern(/^0[1-357]\d{8,9}$/), mobileValidator],
                ],
                telephony: [
                  cd.telephony,
                  [Validators.pattern(/^0[1-35]\d{8,9}$/)],
                ],
                tfn: [
                  cd.tfn,
                  [
                    Validators.pattern(
                      /^(?!BG)(?!GB)(?!NK)(?!KN)(?!TN)(?!NT)(?!ZZ)(?:[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z])(?:\s*\d\s*){6}([A-D]|\s)$/
                    ),
                  ],
                ],
                bsb: [cd.bsb, [Validators.pattern(/^\d{6}$/)]],
                accountNumber: [
                  cd.accountNumber,
                  [Validators.pattern(/^\d{0,12}$/)],
                ],
              },
            };
            break;
          case cd.isCA: {
            const bankDetailsPredicate = () =>
              fg.get('accountName')?.value ||
              fg.get('bank')?.value ||
              fg.get('branch')?.value ||
              fg.get('accountNumber')?.value ||
              fg.get('bsb')?.value;
            fgConfig = {
              ...baseConfig,
              ...{
                phone1: [
                  cd.phone1,
                  [Validators.pattern(/^[23456789]\d{9}$/), phone1Validator],
                ],
                mobile: [
                  cd.mobile,
                  [Validators.pattern(/^[23456789]\d{9}$/), mobileValidator],
                ],
                telephony: [
                  cd.telephony,
                  [Validators.pattern(/^[23456789]\d{9}$/)],
                ],
                accountName: [
                  cd.accountName,
                  [
                    Validators.maxLength(50),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                accountNumber: [
                  cd.accountNumber,
                  [
                    Validators.pattern(/^\d{0,12}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                bsb: [
                  cd.bsb,
                  [
                    Validators.pattern(/^\d{5}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
                branch: [
                  cd.branch,
                  [
                    Validators.pattern(/^\d{3,4}$/),
                    requiredIfValidator(bankDetailsPredicate),
                  ],
                ],
              },
            };
            break;
          }
          case cd.isFJ:
            fgConfig = {
              ...baseConfig,
              ...{
                state: [cd.state, [Validators.maxLength(50)]],
                phone1: [cd.phone1, [Validators.maxLength(7), phone1Validator]],
                mobile: [cd.mobile, [Validators.maxLength(7), mobileValidator]],
                telephony: [
                  cd.telephony,
                  [Validators.pattern(/^[23456789]\d{6}$/)],
                ],
                accountNumber: [
                  cd.accountNumber,
                  [Validators.maxLength(50), Validators.pattern(/^\d+$/)],
                ],
              },
            };
            break;
          default:
            fgConfig = {
              ...baseConfig,
            };
            break;
        }

        fg = this.fb.group(fgConfig);
        return fg;
      }),
      tap((fg) => {
        fg.markAllAsTouched();
      }),
      shareReplayRefCount()
    );

    this.subs.sink = this.formGroup$
      .pipe(
        switchMap((fg) =>
          combineLatest([of(fg), valuesOf<string | null>(fg.get('phone1')!)])
        )
      )
      .subscribe(([fg]) =>
        fg.get('mobile')!.updateValueAndValidity({ emitEvent: false })
      );
    this.subs.sink = this.formGroup$
      .pipe(
        switchMap((fg) =>
          combineLatest([of(fg), valuesOf<string | null>(fg.get('mobile')!)])
        )
      )
      .subscribe(([fg]) =>
        fg.get('phone1')!.updateValueAndValidity({ emitEvent: false })
      );

    this.subs.sink = this.formGroup$
      .pipe(
        switchMap((fg) =>
          combineLatest([
            of(fg),
            valuesOf<string | null>(fg.get('accountName')!),
            valuesOf<string | null>(fg.get('bank')!),
            valuesOf<string | null>(fg.get('branch')!),
            valuesOf<string | null>(fg.get('accountNumber')!),
            valuesOf<string | null>(fg.get('bsb')!),
          ])
        ),
        withLatestFrom(this.contactDetails$)
      )
      .subscribe(([[fg], cd]) => {
        if (cd.isNZ || cd.isCA) {
          fg.get('accountName')!.updateValueAndValidity({ emitEvent: false });
          fg.get('bank')!.updateValueAndValidity({ emitEvent: false });
          fg.get('branch')!.updateValueAndValidity({ emitEvent: false });
          fg.get('accountNumber')!.updateValueAndValidity({ emitEvent: false });
          fg.get('bsb')!.updateValueAndValidity({ emitEvent: false });
        }
      });

    //this.subs.sink = this.formGroup$
    //  .pipe(
    //    switchMap((fg) =>
    //      combineLatest([
    //        of(fg),
    //        valuesOf<string>(fg.get('phone1')!),
    //        valuesOf<string>(fg.get('mobile')!),
    //      ])
    //    )
    //  )
    //  .subscribe(([fg, phone1, mobile]) => {
    //    const phone1Ctrl = fg.get('phone1')!;
    //    const mobileCtrl = fg.get('mobile')!;
    //    phone1Ctrl.removeValidators(Validators.required);
    //    mobileCtrl.removeValidators(Validators.required);
    //    if (!phone1) {
    //      fg.get('mobile')!.addValidators(Validators.required);
    //    }
    //    if (!mobile) {
    //      fg.get('phone1')!.addValidators(Validators.required);
    //    }
    //    phone1Ctrl.updateValueAndValidity({ emitEvent: false });
    //    mobileCtrl.updateValueAndValidity({ emitEvent: false });
    //  });
  }

  private initViewModel() {
    this.viewModel$ = merge<Partial<ViewModel>>(
      this.contactDetails$.pipe(
        map(({ country, isCA, isFJ, isNZ, isUK }) => ({
          country,
          isCA,
          isFJ,
          isNZ,
          isUK,
        }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  cancelAll() {
    this.files.forEach((file) => {
      this.cancelUpload(file.id);
    });

    this.avatarUpload!.nativeElement!.value = '';
  }

  cancelUpload(id: string): void {
    this.uploadInput.emit({ type: 'cancel', id: id });
  }

  ngOnInit(): void {
    this.userSvc.setUpdateDetailsState();
    this.contactDetails$ = this.userSvc.contactDetails$.pipe(
      map((cd) => {
        return {
          ...cd,
          isNZ: collator.compare(cd.country, 'New Zealand') == 0,
          isUK: collator.compare(cd.country, 'United Kingdom') == 0,
          isCA: collator.compare(cd.country, 'Canada') == 0,
          isFJ: collator.compare(cd.country, 'Fiji') == 0,
        } as GetUserContactDetailsVm & CountryFlags;
      }),
      shareReplayRefCount()
    );
    this.initFormGroup();
    this.initViewModel();
  }

  onUploadOutput(output: UploadOutput): void {
    switch (output.type) {
      case 'allAddedToQueue':
        this.startUpload();
        break;
      case 'addedToQueue':
        if (typeof output.file !== 'undefined') {
          this.files.push(output.file);
        }
        break;
      case 'uploading':
        if (typeof output.file !== 'undefined') {
          // update current data in files array for uploading file
          const index = this.files.findIndex(
            (file) =>
              typeof output.file !== 'undefined' && file.id === output.file.id
          );
          this.files[index] = output.file;
        }
        break;
      case 'removed':
        // remove file from array when removed
        this.files = this.files.filter(
          (file: UploadFile) => file !== output.file
        );
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
      case 'done':
        // The file is downloaded
        this.userId$
          .pipe(delay(1000), take(1))
          .subscribe((userId) => this.lookupSvc.refreshAvatars(userId));
        this.avatarUpload!.nativeElement!.value = '';
        break;
    }
  }

  openResetPasswordModal() {
    this.modalSvc.open(ResetPasswordModalComponent, {
      centered: true,
      backdrop: 'static',
    });
  }

  removeAllFiles(): void {
    this.uploadInput.emit({ type: 'removeAll' });
  }

  removeFile(id: string): void {
    this.uploadInput.emit({ type: 'remove', id: id });
  }

  resetAvatar() {
    this.userSvc.resetAvatar();
    this.cancelAll();
    this.userId$
      .pipe(delay(1000), take(1))
      .subscribe((userId) => this.lookupSvc.refreshAvatars(userId));
  }

  showResetConfirmation() {
    this.modalSvc.open(ResetAvatarConfirmationComponent, { size: 'sm' });
  }

  startUpload(): void {
    this.authSvc.accessToken$.pipe(take(1)).subscribe((myToken) => {
      let token = myToken; // <----  get token

      const event: UploadInput = {
        type: 'uploadAll',
        url: `${this.usersApi}/avatar`,
        method: 'POST',
        //data: { foo: 'bar' },
        headers: { Authorization: 'Bearer ' + token }, // <----  set headers
        includeWebKitFormBoundary: true, // <----  set WebKitFormBoundary
        withCredentials: true,
      };

      this.uploadInput.emit(event);
    });
  }

  updateContactDetails() {
    this.formGroup$
      .pipe(take(1), withLatestFrom(this.contactDetails$))
      .subscribe(([fg, cd]) => {
        const mobile = fg.get('mobile');
        const email = fg.get('email');
        const sensitiveDataUpdated = (mobile?.dirty || email?.dirty) ?? false;
        const model: FormModel = fg.getRawValue();
        if (cd.isNZ) {
          const bsb = `${model.bank}${model.branch}`;
          const accountNumber = `${model.accountNumber}${model.bsb}`;
          model.bsb = bsb;
          model.accountNumber = accountNumber;
          model.bank = null;
          model.branch = null;
        }
        if (fg.valid) {
          this.userSvc
            .updateContactDetails(model as any, sensitiveDataUpdated)
            .subscribe(
              (result) => {
                if (result) {
                  this.toastr.success('Contact details updated.');
                  if (fg.get('dob')?.value) {
                    fg.get('dob')?.disable();
                  }
                } else {
                  this.toastr.error('Update cancelled');
                }
              },
              (error) => this.toastr.error('Update failed')
            );
        }
      });
  }
}

interface CountryFlags {
  isCA: boolean;
  isFJ: boolean;
  isNZ: boolean;
  isUK: boolean;
}

interface FormModel
  extends Nullable<Omit<GetUserContactDetailsVm, 'country'>> {}

interface ViewModel extends CountryFlags {
  country?: string;
}
